import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, ChevronRight, Settings, Search, MessageSquareMore, MessageSquare, XCircle, Loader2, Palette, Layers, User, BookOpen } from 'lucide-react';
import { NewChatIcon, ChatsIcon } from '@/components/icons/CustomIcons';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import axios from 'axios';
import { getApiEndpoint } from '@/utils/ERPUtils';
import { useAuth } from '@/context/AuthContext';
import { CATEGORY_CONFIG, getPathForModule, getPathForAdminModule } from '@/utils/moduleMapping';
import { toast } from 'sonner';
import { useYourChats, CHAT_CATEGORY_CONFIG } from '@/hooks/useYourChats';
import { useChatSessionSearch } from '@/hooks/useChatSessionSearch';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useNavigate } from 'react-router-dom';
import { CLOUD_SOURCES_CHANGED_EVENT } from '@/services/cloudSourceService';

import { logger } from '@/utils/logger';
interface SubNavigationItem {
  name: string;
  section: string;
  children?: { name: string; section: string }[];
}

interface NavigationItem {
  name: string;
  icon: React.ComponentType<any>;
  section: string;
  subItems?: SubNavigationItem[];
}

interface MobileNavigationProps {
  isOpen: boolean;
  onClose: () => void;
  onSectionChange: (section: string) => void;
  activeSection: string;
}


/**
 * Converts desktop route paths to mobile section identifiers
 * Example: '/dashboard/admin/users' -> 'admin/users'
 */
const pathToSection = (path: string): string => {
  // Remove leading slash and 'dashboard/' prefix if present
  return path.replace(/^\//, '').replace(/^dashboard\//, '');
};

export const MobileNavigation: React.FC<MobileNavigationProps> = ({
  isOpen,
  onClose,
  onSectionChange,
  activeSection
}) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { roles: userRoles } = useUserRoles();
  const isSystemManager = userRoles?.some(r => r.name === 'System Manager');
  const [allowedModules, setAllowedModules] = useState<any>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [expandedSubItems, setExpandedSubItems] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Your Chats - using shared hook
  const {
    yourChatsExpanded,
    setYourChatsExpanded,
    expandedCategories,
    chatsByCategory,
    loadingByCategory,
    loadingMoreByCategory,
    paginationByCategory,
    totalSessionsCount,
    toggleCategory,
    loadMoreForCategory,
    fetchAllCategories,
  } = useYourChats({ autoFetchOnMount: false, pageSize: 5 });

  // Refs for scroll containers to detect infinite scroll
  const scrollRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Fetch counts for ALL categories when navigation panel opens
  useEffect(() => {
    if (isOpen) {
      fetchAllCategories();
    }
  }, [isOpen, fetchAllCategories]);

  // Infinite scroll handler for chat list
  const handleChatListScroll = useCallback((category: string) => {
    const scrollContainer = scrollRefs.current[category];
    if (!scrollContainer) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50; // 50px threshold

    if (isNearBottom) {
      loadMoreForCategory(category);
    }
  }, [loadMoreForCategory]);

  // Auto-load more if content doesn't overflow but more pages exist
  useEffect(() => {
    // Small delay to wait for DOM to update after new content is added
    const timer = setTimeout(() => {
      expandedCategories.forEach(categoryId => {
        const scrollContainer = scrollRefs.current[categoryId];
        const pagination = paginationByCategory[categoryId];
        const isLoadingMore = loadingMoreByCategory[categoryId];
        const isLoading = loadingByCategory[categoryId];

        if (scrollContainer && pagination && !isLoadingMore && !isLoading) {
          const hasMorePages = pagination.currentPage < pagination.totalPages;
          const contentFits = scrollContainer.scrollHeight <= scrollContainer.clientHeight;

          // If content fits (no scroll) but there are more pages, auto-load
          if (contentFits && hasMorePages) {
            loadMoreForCategory(categoryId);
          }
        }
      });
    }, 100); // 100ms delay for DOM to update

    return () => clearTimeout(timer);
  }, [expandedCategories, paginationByCategory, loadingMoreByCategory, loadingByCategory, loadMoreForCategory, chatsByCategory]);

  // ERP chat session search
  const {
    sessions: erpSessions,
    isLoading: isSearching,
    searchSessions,
    clearResults
  } = useChatSessionSearch();

  // Debounced search effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery.trim().length > 2) {
        searchSessions(searchQuery);
      } else {
        clearResults();
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, searchSessions, clearResults]);

  // Format date relatively (today, yesterday, etc.)
  const formatRelativeDate = useCallback((dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
    return `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
  }, []);

  // Handle opening a chat session - all categories route to /dashboard
  // since General is the only chat category.
  const handleOpenChatSession = useCallback((session: any, _category?: string) => {
    onClose();
    navigate(`/dashboard?session_id=${session.name}`);
  }, [navigate, onClose]);

  // Fetch allowed modules from backend (same as desktop)
  useEffect(() => {
    const fetchAllowedModules = async () => {
      try {
        const user = auth.user;
        const response = await axios.get(getApiEndpoint('GET_ALLOWED_MODULES'));

        if (response?.data?.message?.status === 'error') {
          throw Error(response?.data?.message?.message);
        }

        const userRoleSet = new Set(user?.roles || []);
        const matchingRoleEntries = response.data?.message?.data?.filter(
          (e: any) => userRoleSet.has(e?.role)
        ) || [];
        const moduleMap = new Map<string, any>();
        for (const entry of matchingRoleEntries) {
          for (const mod of (entry.modules || [])) {
            const key = mod.module;
            if (!moduleMap.has(key) || !moduleMap.get(key).show) {
              moduleMap.set(key, { ...mod });
            } else if (mod.sub_modules?.length) {
              const existing = moduleMap.get(key);
              const existingSubs = new Set((existing.sub_modules || []).map((s: any) => s.module));
              for (const sub of mod.sub_modules) {
                if (!existingSubs.has(sub.module)) {
                  existing.sub_modules = [...(existing.sub_modules || []), sub];
                }
              }
            }
          }
        }
        const modules = moduleMap.size > 0 ? Array.from(moduleMap.values()) : undefined;

        setAllowedModules(modules);
      } catch (error) {
        logger.error('[MobileNavigation] Error fetching allowed modules:', error);
      }
    };

    if (auth.user) {
      fetchAllowedModules();
    }
  }, [auth.user]);

  // Same gating as the desktop sidebar: with a live connector, manual
  // upload navigation is hidden (ingestion flows through the connector).
  const [hasConnectedSource, setHasConnectedSource] = useState(false);
  const refreshConnectedSource = useCallback(async () => {
    try {
      const res = await axios.get(getApiEndpoint('CLOUD_HAS_CONNECTED_SOURCE'), { withCredentials: true });
      setHasConnectedSource(!!res.data?.message?.connected);
    } catch {
      /* keep nav visible if the check fails */
    }
  }, []);
  useEffect(() => {
    refreshConnectedSource();
  }, [refreshConnectedSource]);
  // A connect/disconnect/delete broadcasts this event so the nav updates live,
  // without waiting for a page reload.
  useEffect(() => {
    const handler = () => refreshConnectedSource();
    window.addEventListener(CLOUD_SOURCES_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CLOUD_SOURCES_CHANGED_EVENT, handler);
  }, [refreshConnectedSource]);

  // Generate dynamic navigation items based on allowedModules (same as desktop)
  const navigationItems = useMemo((): NavigationItem[] => {
    if (!allowedModules) return [];

    // Collect items by type for explicit ordering (matching desktop sidebar)
    let newChatItem: NavigationItem | null = null;
    let customerItem: NavigationItem | null = null;
    let knowledgeItem: NavigationItem | null = null;
    let administrationItem: NavigationItem | null = null;
    const otherItems: NavigationItem[] = [];

    allowedModules.forEach((moduleGroup: any) => {
      const moduleName = moduleGroup.module;
      const shouldShow = moduleGroup.show === 1;

      if (!shouldShow) return;

      // Handle modules with sub_modules
      if (moduleGroup.sub_modules && moduleGroup.sub_modules.length > 0) {
        // Modules to hide on mobile sidebar
        const mobileHiddenModules = ['Landscape Design'];

        const visibleSubModules = moduleGroup.sub_modules.filter(
          (subModule: any) => subModule.show === 1 && !mobileHiddenModules.includes(subModule.module)
        );

        if (visibleSubModules.length > 0) {
          const categoryKey = moduleName.toLowerCase();
          const categoryConfig = CATEGORY_CONFIG[categoryKey];

          if (moduleName === 'Sales') {
            // Handle Sales specially - show single "Customer" menu that goes to CRM
            const hasCRM = visibleSubModules.some((sub: any) => sub.module === 'CRM');
            if (hasCRM) {
              customerItem = {
                name: 'Customer',
                icon: User,
                section: 'crm'
              };
            }
          } else if (moduleName === 'Administration') {
            // Handle Administration specially - show User Management and My Company on mobile
            const adminSubItems = visibleSubModules
              .filter((subModule: any) => subModule.module === 'User Management' || subModule.module === 'My Company')
              .map((subModule: any) => {
                const path = getPathForAdminModule(subModule.module);
                return {
                  name: subModule.module,
                  section: path ? pathToSection(path) : '#'
                };
              });

            // Check original sub_modules (not filtered) for Knowledge Base enablement
            const hasKnowledgeBase = moduleGroup.sub_modules?.some(
              (subModule: any) => subModule.module === 'Knowledge Base' && subModule.show === 1
            );

            if (hasKnowledgeBase) {
              // Nest KB children under a Knowledge Base parent (mirrors desktop)
              const kbChildren: { name: string; section: string }[] = [
                // Admin Document Library always stays - it's the doc view. Only
                // the manual Upload Documents child drops out while a connector
                // is live; Cloud Import (the connector surface) remains.
                ...(hasConnectedSource
                  ? []
                  : [{ name: 'Upload Documents', section: pathToSection('/dashboard/admin/upload') }]),
                { name: 'Cloud Import', section: pathToSection('/dashboard/admin/cloud-import') },
                { name: 'Document Library', section: pathToSection('/dashboard/admin/knowledge-base') },
              ];

              // System Manager-only KB pages (mirrors desktop gating)
              if (isSystemManager) {
                kbChildren.push(
                  { name: 'Review Queue', section: pathToSection('/dashboard/admin/review-queue') },
                  { name: 'Entity Types', section: pathToSection('/dashboard/admin/entity-types') },
                  { name: 'Entity QA', section: pathToSection('/dashboard/admin/entity-qa') },
                );
              }

              adminSubItems.push({
                name: 'Knowledge Base',
                section: pathToSection('/dashboard/admin/knowledge-base'),
                children: kbChildren,
              });
            }

            // Only add Administration if sub-items are available
            if (adminSubItems.length > 0) {
              administrationItem = {
                name: 'Administration',
                icon: Settings,
                section: 'admin',
                subItems: adminSubItems
              };
            }
          } else if (categoryConfig) {
            const subItems = visibleSubModules.map((subModule: any) => {
              const path = getPathForModule(subModule.module);
              return {
                name: subModule.module,
                section: path ? pathToSection(path) : '#'
              };
            });

            let displayName = moduleName;

            otherItems.push({
              name: displayName,
              icon: categoryConfig.icon,
              section: categoryKey,
              subItems
            });
          }
        }
      } else {
        // Handle single modules without sub_modules
        if (moduleName === 'Dashboard') {
          newChatItem = {
            name: 'New Chat',
            icon: MessageSquareMore,
            section: 'dashboard'
          };
        } else if (moduleName === 'Documents') {
          if (!hasConnectedSource) {
            knowledgeItem = {
              name: 'Documents',
              icon: BookOpen,
              section: 'knowledge'
            };
          }
        } else {
          const categoryConfig = CATEGORY_CONFIG[moduleName.toLowerCase()];
          if (categoryConfig) {
            const path = getPathForModule(moduleName);
            otherItems.push({
              name: moduleName,
              icon: categoryConfig.icon,
              section: path ? pathToSection(path) : moduleName.toLowerCase()
            });
          }
        }
      }
    });

    // Build menu in specific order: New Chat, Knowledge, Customer, Administration
    const items: NavigationItem[] = [];
    if (newChatItem) items.push(newChatItem);
    if (knowledgeItem) items.push(knowledgeItem);
    if (customerItem) items.push(customerItem);
    if (administrationItem) items.push(administrationItem);
    items.push(...otherItems);

    return items;
  }, [allowedModules, isSystemManager, hasConnectedSource]);


  const toggleExpanded = (itemName: string) => {
    // Add haptic feedback for navigation interactions
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }

    // Close Chats section when opening a navigation accordion
    setYourChatsExpanded(false);

    // Close others when opening a new one (single accordion behavior)
    setExpandedItems(prev =>
      prev.includes(itemName)
        ? [] // Close if already open
        : [itemName] // Open only this one, close others
    );
  };

  const handleItemClick = (section: string, hasSubItems: boolean, itemName?: string) => {
    // Add haptic feedback for navigation
    if ('vibrate' in navigator) {
      navigator.vibrate(30);
    }

    if (hasSubItems && itemName) {
      toggleExpanded(itemName);
    } else {
      onSectionChange(section);
    }
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 animate-in fade-in-0 duration-200"
          onClick={onClose}
        />
      )}
      
      {/* Full screen panel */}
      <div
        className={`fixed inset-0 w-full h-full bg-background text-foreground transform transition-all duration-300 ease-out z-50 will-change-transform flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)'
        }}
      >
        {/* Header */}
       <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <h2 className="text-xl font-semibold">Menu</h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center h-10 w-10 rounded-full border-2 border-foreground hover:bg-accent transition-colors"
          >
            <X className="h-5 w-5" strokeWidth={2.5} />
          </button>
        </div>

          {/* Search Box */}
        <div className="px-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search chats..."
              className="w-full pl-10 pr-10 py-2.5 bg-muted border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <XCircle className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Results count */}
          {searchQuery.trim().length > 2 && (
            <div className="mt-2 pt-3 px-2 text-xs text-muted-foreground flex items-center gap-2">
              {isSearching ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Searching...</span>
                </>
              ) : (
                <span>{erpSessions.length} {erpSessions.length === 1 ? 'result' : 'results'} found</span>
              )}
            </div>
          )}
        </div>

        {/* Search Results - replaces menu when searching */}
        {searchQuery.trim().length > 2 && (
          <ScrollArea className="flex-1 min-h-0 overflow-y-auto">
            <div className="px-4 py-2">
              {isSearching ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : erpSessions.length > 0 ? (
                <div className="space-y-2">
                  {erpSessions.map((session) => (
                    <button
                      key={session.session_id}
                      onClick={() => handleOpenChatSession(session)}
                      className="flex items-start justify-between w-full text-left px-4 py-3 text-sm text-accent-foreground bg-accent rounded-xl shadow-sm hover:shadow-md transition-all gap-2"
                    >
                      <div className="flex-1 min-w-0">
                        <span className="font-medium block truncate">{session.session_title}</span>
                        <span className="text-muted-foreground text-xs block">{formatRelativeDate(session.started_at)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="px-3 py-8 text-sm text-muted-foreground text-center">
                  No results for "{searchQuery}"
                </div>
              )}
            </div>
          </ScrollArea>
        )}

        {/* Navigation Items - hidden when searching */}
        {!searchQuery.trim() && (
        <ScrollArea className="flex-1 min-h-0 overflow-y-auto">
          <div className="px-4 py-2 space-y-1">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isExpanded = expandedItems.includes(item.name);
              const isActive = activeSection === item.section ||
                              activeSection.startsWith(item.section + '/');
              const hasSubItems = Boolean(item.subItems?.length);

              const renderNameWithTM = (name: string): React.ReactNode => name;

              // Render navigation icon based on item name (matches desktop expanded sizes)
              const renderNavIcon = () => {
                if (item.name === 'New Chat') {
                  return <NewChatIcon className="h-10 w-10 mr-3 flex-shrink-0" />;
                }
                if (item.name === 'Customer') {
                  return <User className="h-8 w-8 mr-3 flex-shrink-0 text-foreground" />;
                }
                if (item.name === 'Administration') {
                  return <Settings className="h-8 w-8 mr-3 flex-shrink-0 text-foreground" />;
                }
                return <Icon className="h-8 w-8 mr-3 flex-shrink-0 text-foreground" />;
              };

              return (
                <div key={item.name}>
                  <Button
                    variant="ghost"
                    className={`w-full flex items-center justify-between px-4 py-3.5 rounded-lg text-left transition-all ${
                    isActive
                        ? 'bg-accent text-accent-foreground shadow-sm'
                        : 'hover:bg-accent'
                    }`}
                    onClick={() => handleItemClick(item.section, !!item.subItems, item.name)}
                  >
                    {renderNavIcon()}
                    <span className="flex-1 font-medium text-[15px]">{renderNameWithTM(item.name)}</span>
                    {item.subItems && (
                      <ChevronRight 
                        className={`h-4 w-4 flex-shrink-0 transition-transform ${
                          isExpanded ? 'rotate-90' : ''
                        }`} 
                      />
                    )}
                  </Button>
                  
                  {/* Sub-items */}
                  {hasSubItems && isExpanded && (
                      <div className="ml-10 mt-1 mb-2 space-y-1">
                        {item.subItems?.map((subItem) => {
                          const isSubActive = activeSection === subItem.section;
                          const hasChildren = Boolean(subItem.children?.length);
                          const subKey = `${item.name}:${subItem.name}`;
                          const isSubExpanded = expandedSubItems.includes(subKey);
                          return (
                            <div key={subItem.name}>
                              <button
                                onClick={() => {
                                  if (hasChildren) {
                                    if ('vibrate' in navigator) navigator.vibrate(30);
                                    setExpandedSubItems(prev =>
                                      prev.includes(subKey) ? [] : [subKey]
                                    );
                                  } else {
                                    onSectionChange(subItem.section);
                                  }
                                }}
                                className={`w-full flex items-center justify-between text-left px-4 py-2.5 rounded-md text-[14px] transition-all ${
                                  isSubActive
                                    ? 'bg-accent text-accent-foreground font-medium'
                                    : ''
                                }`}
                              >
                                <span>{subItem.name}</span>
                                {hasChildren && (
                                  <ChevronRight
                                    className={`h-4 w-4 flex-shrink-0 transition-transform ${
                                      isSubExpanded ? 'rotate-90' : ''
                                    }`}
                                  />
                                )}
                              </button>

                              {hasChildren && isSubExpanded && (
                                <div className="ml-4 mt-1 mb-1 space-y-1">
                                  {subItem.children?.map((child) => {
                                    const isChildActive = activeSection === child.section;
                                    return (
                                      <button
                                        key={child.name}
                                        onClick={() => onSectionChange(child.section)}
                                        className={`w-full text-left px-4 py-2 rounded-md text-[13px] transition-all ${
                                          isChildActive
                                            ? 'bg-accent text-accent-foreground font-medium'
                                            : ''
                                        }`}
                                      >
                                        {child.name}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                      })}
                      </div>
                    )}
                </div>
              );
            })}
              {/* Your Chats Section */}
              <div>
                <button
                  onClick={() => {
                    // Close navigation accordions when opening Chats
                    setExpandedItems([]);
                    const next = !yourChatsExpanded;
                    setYourChatsExpanded(next);
                    if (next) {
                      // Auto-load General sessions when opening Recent Chats
                      // (mirrors desktop's flat list - no inner accordion).
                      const cat = CHAT_CATEGORY_CONFIG[0];
                      if (!expandedCategories.includes(cat.id)) {
                        toggleCategory(cat.id);
                      }
                    }
                  }}
                  className="w-full flex items-center justify-between px-4 py-3.5 rounded-lg text-left transition-all"
                >
                  <div className="flex items-center">
                    <ChatsIcon className="h-6 w-6 mr-3 flex-shrink-0" />
                    <span className="flex-1 font-medium text-[15px] mr-2">Recent Chats</span>
                    {totalSessionsCount > 0 && (
                      <span className="text-xs px-2 py-0.5 bg-muted rounded-full mr-2">
                        {totalSessionsCount}
                      </span>
                    )}
                  </div>
                  <ChevronRight
                    className={`h-4 w-4 transition-transform duration-200 ${
                      yourChatsExpanded ? 'rotate-90' : ''
                    }`}
                    strokeWidth={2.5}
                  />
                </button>

                {/* Recent chats - flat list (no General sub-header, mirrors desktop). */}
                {yourChatsExpanded && (() => {
                  const category = CHAT_CATEGORY_CONFIG[0];
                  const categorySessions = chatsByCategory[category.id] || [];
                  const isLoading = loadingByCategory[category.id];
                  const isLoadingMore = loadingMoreByCategory[category.id];

                  return (
                    <div
                      ref={(el) => { scrollRefs.current[category.id] = el; }}
                      onScroll={() => handleChatListScroll(category.id)}
                      className="ml-8 mt-1 mb-2 space-y-0.5 overflow-y-auto"
                      style={categorySessions.length >= 5 ? { minHeight: '250px', maxHeight: '250px' } : {}}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        </div>
                      ) : categorySessions.length > 0 ? (
                        <>
                          {categorySessions.map((session: any) => (
                            <button
                              key={session.name}
                              onClick={() => handleOpenChatSession(session, category.id)}
                              className="w-full text-left px-3 py-2 rounded-md transition-all hover:bg-accent"
                            >
                              <p className="text-[13px] truncate pr-2">{session.session_title}</p>
                              <p className="text-[10px] text-muted-foreground">{formatRelativeDate(session.started_at)}</p>
                            </button>
                          ))}
                          {isLoadingMore && (
                            <div className="flex items-center justify-center gap-2 px-3 py-2">
                              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                              <span className="text-[11px] text-muted-foreground">Loading more...</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-[12px] text-muted-foreground px-3 py-2">No chats yet</p>
                      )}
                    </div>
                  );
                })()}
              </div>
          </div>
        </ScrollArea>
        )}

        {/* Footer - Settings and Train AI - always at bottom */}
        <div className="mt-auto px-4 pt-4 space-y-1" style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}>
            <Button
            className="w-full flex items-center justify-center px-4 py-3.5 mb-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all"
            onClick={() => {
              toast.success('Invite link copied!');
            }}
          >
            <span className="font-medium text-[15px]">Invite</span>
          </Button>

          {/* BETA Badge */}
          <div className="flex justify-center py-2">
            <span className="px-4 py-1 text-sm font-bold tracking-[0.25rem] rounded-full">
              BETA
            </span>
          </div>

          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground hidden hover:bg-accent hover:text-accent-foreground active:bg-accent h-11 min-h-[44px]"
            onClick={() => onSectionChange('settings')}
          >
            <Settings className="h-5 w-5 mr-3 flex-shrink-0" />
            <span className="text-sm font-medium">Settings</span>
          </Button>
          
        </div>
      </div>
    </>
  );
};